import React, { useState } from "react";
import { useLang } from "@/context/LanguageContext";
import PaxFields, { DEFAULT_PAX_CONFIG } from "@/components/pax/PaxFields";
import PaxPricePanel from "@/components/pax/PaxPricePanel";
import PaxUpload from "@/components/pax/PaxUpload";
import { UploadCloud, SlidersHorizontal, CheckCircle2, AlertTriangle, RotateCcw } from "lucide-react";

// Orchestrates the two ways to get a PAX price:
//  A) Upload IKEA order/plan -> AI recognition -> confirmation screen -> price
//  B) Manual calculator
// Both paths feed the SAME pricing engine via PaxPricePanel.
export default function PaxCalculator() {
  const { t } = useLang();
  const [mode, setMode] = useState("upload"); // upload | manual
  const [config, setConfig] = useState(DEFAULT_PAX_CONFIG);
  const [postcode, setPostcode] = useState("");
  const [recognition, setRecognition] = useState(null); // {items, low_confidence, ...}
  const [confirmed, setConfirmed] = useState(false);
  const [source, setSource] = useState("manual");
  const [recognizedComponents, setRecognizedComponents] = useState([]);
  const [uploadedFileIds, setUploadedFileIds] = useState([]);

  const onRecognized = ({ fileId, recognition }) => {
    const next = { ...DEFAULT_PAX_CONFIG };
    ["sections", "hinged_doors", "mirror_doors", "sliding_doors", "handles", "drawers", "baskets", "shelves", "rails",
     "trays", "trouser_hangers", "multi_hangers", "other_pullouts", "shoe_shelf", "frame_dividers", "corner_sections", "lighting", "other_unknown"]
      .forEach((k) => { if (typeof recognition[k] === "number") next[k] = recognition[k]; });
    if (recognition.frame_width) next.frame_width = recognition.frame_width;
    if (recognition.frame_height) next.frame_height = recognition.frame_height;
    if (next.sections < 1) next.sections = 1;
    next.low_confidence = !!recognition.low_confidence;
    setConfig(next);
    setRecognition(recognition);
    setRecognizedComponents(recognition.items || []);
    setUploadedFileIds(fileId ? [fileId] : []);
    setSource("upload");
    setConfirmed(false);
  };

  const resetUpload = () => { setRecognition(null); setConfirmed(false); setSource("manual"); setRecognizedComponents([]); setUploadedFileIds([]); setConfig(DEFAULT_PAX_CONFIG); };

  const showFields = mode === "manual" || (mode === "upload" && recognition);
  const showPrice = mode === "manual" || (mode === "upload" && recognition && confirmed);

  return (
    <div data-testid="pax-calculator">
      {/* Method chooser */}
      <div className="grid sm:grid-cols-2 gap-3 mb-6">
        <button type="button" onClick={() => setMode("upload")}
          className={`relative flex items-center gap-3 p-4 rounded-2xl border text-left transition-colors ${mode === "upload" ? "border-[#1E3A2F] bg-[#1E3A2F]/5" : "border-stone-200 hover:bg-stone-50"}`}
          data-testid="pax-mode-upload">
          <span className="absolute top-3 right-3 text-[10px] font-bold uppercase text-emerald-800 bg-emerald-100 px-2 py-0.5 rounded-full">{t.pax.recommended}</span>
          <div className="w-11 h-11 rounded-xl bg-[#1E3A2F] flex items-center justify-center shrink-0"><UploadCloud className="w-5 h-5 text-white" /></div>
          <div><div className="font-semibold text-stone-900">{t.pax.uploadOption}</div><div className="text-xs text-stone-500">{t.pax.uploadFast}</div></div>
        </button>
        <button type="button" onClick={() => setMode("manual")}
          className={`flex items-center gap-3 p-4 rounded-2xl border text-left transition-colors ${mode === "manual" ? "border-[#1E3A2F] bg-[#1E3A2F]/5" : "border-stone-200 hover:bg-stone-50"}`}
          data-testid="pax-mode-manual">
          <div className="w-11 h-11 rounded-xl bg-stone-100 flex items-center justify-center shrink-0"><SlidersHorizontal className="w-5 h-5 text-[#1E3A2F]" /></div>
          <div><div className="font-semibold text-stone-900">{t.pax.manualOption}</div><div className="text-xs text-stone-500">{t.pax.orManual}</div></div>
        </button>
      </div>

      <div className="grid lg:grid-cols-5 gap-6">
        <div className="lg:col-span-3 space-y-6">
          {mode === "upload" && !recognition && <PaxUpload onRecognized={onRecognized} />}

          {mode === "upload" && recognition && !confirmed && (
            <div className="bg-white border border-stone-200 rounded-2xl p-5 sm:p-7" data-testid="pax-confirmation">
              <div className="flex items-center gap-2 text-[#1E3A2F]"><CheckCircle2 className="w-5 h-5" /><h3 className="font-display text-xl font-bold">{t.pax.foundTitle}</h3></div>
              <p className="text-sm text-stone-500 mt-1">{t.pax.foundSubtitle}</p>

              {recognition.low_confidence && (
                <p className="mt-3 flex items-start gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 rounded-lg p-3" data-testid="pax-low-confidence">
                  <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" /> {t.pax.lowConfidence}
                </p>
              )}

              {recognizedComponents.length > 0 && (
                <div className="mt-4">
                  <div className="text-xs uppercase tracking-widest text-stone-400 font-semibold mb-2">{t.pax.recognizedItems}</div>
                  <ul className="space-y-1 text-sm max-h-40 overflow-y-auto pr-1">
                    {recognizedComponents.map((it, i) => (
                      <li key={i} className={`flex justify-between gap-2 ${it.confidence < 0.6 ? "text-amber-700" : "text-stone-600"}`} data-testid={`recognized-item-${i}`}>
                        <span className="truncate">{it.quantity}× {it.name || it.category}{it.article ? ` (${it.article})` : ""}</span>
                        <span className="font-mono text-xs shrink-0">{Math.round((it.confidence || 0) * 100)}%</span>
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              <div className="mt-5 border-t border-stone-100 pt-4">
                <PaxFields config={config} onChange={setConfig} />
              </div>

              <div className="mt-5 flex flex-col sm:flex-row gap-3">
                <button onClick={() => setConfirmed(true)} className="flex-1 py-3.5 rounded-full bg-[#1E3A2F] text-white font-bold hover:bg-[#162D24]" data-testid="pax-confirm-btn">
                  {t.pax.confirmContinue}
                </button>
                <button onClick={resetUpload} className="inline-flex items-center justify-center gap-2 px-5 py-3.5 rounded-full border border-stone-300 font-semibold text-stone-700 hover:bg-stone-50" data-testid="pax-startover-btn">
                  <RotateCcw className="w-4 h-4" /> {t.pax.startOver}
                </button>
              </div>
            </div>
          )}

          {mode === "upload" && recognition && confirmed && (
            <div className="bg-white border border-stone-200 rounded-2xl p-5 sm:p-7" data-testid="pax-confirmed-fields">
              <div className="flex items-center justify-between mb-3">
                <h3 className="font-display text-lg font-bold text-stone-900">{t.pax.editManually}</h3>
                <button onClick={resetUpload} className="inline-flex items-center gap-1.5 text-sm font-semibold text-stone-500 hover:text-stone-800" data-testid="pax-startover-btn-2">
                  <RotateCcw className="w-4 h-4" /> {t.pax.startOver}
                </button>
              </div>
              <PaxFields config={config} onChange={setConfig} />
            </div>
          )}

          {mode === "manual" && (
            <div className="bg-white border border-stone-200 rounded-2xl p-5 sm:p-7" data-testid="pax-manual">
              <h3 className="font-display text-xl font-bold text-stone-900">{t.pax.calcTitle}</h3>
              <p className="text-sm text-stone-500 mt-1 mb-4">{t.pax.calcSubtitle}</p>
              <PaxFields config={config} onChange={setConfig} />
            </div>
          )}
        </div>

        <div className="lg:col-span-2">
          {showPrice ? (
            <PaxPricePanel config={config} postcode={postcode} setPostcode={setPostcode} source={source} recognizedComponents={recognizedComponents} fileIds={uploadedFileIds} />
          ) : (
            <div className="lg:sticky lg:top-24 bg-[#F4F1EA] border border-stone-200 rounded-2xl p-6 text-center text-stone-500" data-testid="pax-price-placeholder">
              <p className="text-sm">{mode === "upload" ? t.pax.uploadDesc : t.pax.calcSubtitle}</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
